//app.js
import React, { useState, useMemo, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "react-query";
import { useMediaQuery, Box, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { CreatorAuthProvider } from "./Hooks/creator-use-auth.js";
import useAuth from "./Hooks/use-auth.js";
import client from "./API";
import routes from "./Config/routes.js";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import HelpIcon from "@mui/icons-material/Help";
import PageTitle from "./Components/PageTitle.js";
import Main from "./Pages/Home/Home";

// ═══════════════════════════════════════════════════════════
// TUTORIAL SYSTEM IMPORTS
// ═══════════════════════════════════════════════════════════
import { TutorialProvider } from "./BlitzHowTo/tutorialContext.js";
import { TutorialOverlay } from "./BlitzHowTo/tutorialOverlay";

// ═══════════════════════════════════════════════════════════
// SENTRY ERROR TRACKING IMPORTS
// ═══════════════════════════════════════════════════════════
import { SentryErrorBoundary } from "./Components/ErrorBoundary.js";
import { ToastContainer } from "react-toastify";
import { Sentry } from "./Utils/sentryConfig.js";
import { Soc2Banner } from "./Components/banners/soc2banner.js";
import StripeOnboardingReturnHandler from "./Components/payments/StripeOnboardingReturnHandler";
import { Analytics } from "@vercel/analytics/react";

// Add CSS animation for lightning bolt
const globalStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}
import Register from "./Pages/Login/Register";
import Dashboard from "./Pages/Dashboard/dashboard/dashboard.js";
import User from "./Pages/Dashboard/user.js";
import Invoicing from "./Pages/Dashboard/invoicing/invoicing.js";
import Search from "./Pages/Dashboard/Search/search";
import CreatorSignup from "./Pages/Home/auxLinks/creatorSignup.js";
import BlitzPay from "./Pages/BlitzPay/blitzpay.js";
import InternalTransfer from "./Pages/BlitzPay/InternalTransfer.js";
import PayoutRouting from "./Pages/BlitzPay/PayoutRouting.js";
import AddCreators from "./Pages/Dashboard/Campaigns/addCreators.js";
import PrivateRoute from "./Lib/private-route.js";
import CreatorRoute from "./Lib/creator-route.js";
import GlobalRoute from "./Lib/global-route.js";
import CompanyList from "./Pages/Dashboard/Company/index.js";
import CampaignDetailsPage from "./Pages/Dashboard/Campaigns/individualcampaigns/campaignDetailsPage.js";
import AgencyDetailsPage from "./Pages/Dashboard/Creators/agency/agencyDetail.js";
import CreatorConnect from "./CreatorUser/CreatorPages/creatorConnect.js";
import CreatorStart from "./CreatorUser/CreatorPages/Dashboard/start.js";
import CreatorInvoicePage from "./CreatorUser/CreatorPages/Dashboard/misc/creatorinvoicepage.js";
import InvoicePage from "./Pages/Dashboard/invoicing/invoicepage.js";
import HelpDialog from "./helpdialog.js";
import InternalLogin from "./CreatorUser/internalLogin.js";
import RequestAccess from "./Pages/Home/auxLinks/requestAccess.js";
import SimilarCreators from "./Pages/Dashboard/Creators/similarcreators.js";
import DemoRegister from "./Pages/Home/auxLinks/demoRegister.js";
import BlitzSummary from "./Pages/Home/blitzsummary.js";
import CreatorCampaignTimelines from "./Pages/Dashboard/Campaigns/individualcampaigns/creatorcampaigntimelines.js";
import CreatorDeliverables from "./Pages/Dashboard/Campaigns/projectcomponents/deliverablepage.js";
import BlitzRoute from "./Components/BlitzRoute.js";
import TempCreatorPage from "./Pages/Dashboard/Creators/tempcreatorpage.js";
import HelpCenter from "./Pages/Misc/helppage.js";
import AssetsManager from "./Pages/Misc/assetsDemo.js";
import EditCampaignPage from "./Pages/Dashboard/Campaigns/campaignsDialog/editCampaignPage.js";
import { getTheme } from "./Utils/baseTheme.js";
import PartnershipsPage from "./Pages/Dashboard/Campaigns/projectcomponents/partnershipspage.js";
import CreatorConnectReatuh from "./CreatorUser/CreatorPages/creatorConnectReauth.js";
import Solutions from "./Pages/Home/brandbenefits/pay.js";
import ForCreators from "./Pages/Home/forcreators/forcreators.js";
import PayoutsInvoicing from "./CreatorUser/CreatorPages/Dashboard/payoutsinvoicing.js";
import CreatorInternalTransfer from "./CreatorUser/CreatorPages/Dashboard/InternalTransfer.js";
import Pitch from "./CreatorUser/CreatorPages/Dashboard/pitch.js";
import EditCreator from "./CreatorUser/CreatorPages/Dashboard/userInfo.js";
import BrandEmails from "./CreatorUser/CreatorPages/Dashboard/brandEmails.js";
import CreatorMore from "./CreatorUser/CreatorPages/Dashboard/misc/creatorMore.js";
import CreatorConnectRoute from "./Components/CreatorConnectRoute.js";
import AgencyStart from "./Pages/Agency/agencyStart.js";
import CreatorSpecificCampaign from "./CreatorUser/CreatorPages/Dashboard/creatorcomponents/creatorspecificcampaign.js";
import WhyPartner from "./Pages/Home/forcreators/whypartner.js";
import Trackers from "./Pages/Trackers/trackers.js";
import CreatorSpecificPartnership from "./CreatorUser/CreatorPages/Dashboard/partnershipcomps/creatorspecificpartnerships.js";
import PitchFromZap from "./CreatorUser/CreatorPages/Dashboard/zapfeed/pitchtozapfeed.js";
import PartnerCreatorRoster from "./Pages/Misc/partnerspage.js";
import CampaignCreatorSpecific from "./Pages/Dashboard/Campaigns/campaignsDialog/conversations/campaigncreatorspec.js";
import PrivacyPolicy from "./Pages/Home/privacypolicy.js";
import AgencyRoot from "./Pages/Agency/agencyRoot.js";
import AgencyCreators from "./Pages/Agency/agencyCreators.js";
import AgencyCreator from "./Pages/Agency/agencyCreator.js";
import OptOutCampaign from "./Pages/Dashboard/Campaigns/individualcampaigns/optoutcampaign.js";
import CreatorRedirect from "./CreatorUser/redirect.js";
import Collaborations from "./Pages/Dashboard/Campaigns/collabs.js";
import CreatorCollaborations from "./CreatorUser/CreatorPages/Dashboard/creatorcollabs.js";
import HottestNewCreatorSoftware from "./Pages/Home/articles/1.js";
import FutureOfPayments from "./Pages/Home/articles/2.js";
import StreamlineCampaigns from "./Pages/Home/articles/3.js";
import Agencies from "./Pages/Home/brandbenefits/agencies.js";
import PayoutPage from "./Pages/BlitzPay/blitzpayspec.js";
import DealIOPublic from "./Pages/DealsIO/dealIOPublic.js";
import AllOnboard from "./Pages/Misc/AllOnboard.js";
import DocsRoute from "./Pages/Documentation/DocsRoute.js";
import DocsHomepage from "./Pages/Documentation/Homepage.js";
import DocsServices from "./Pages/Documentation/DocsServices.js";
import DocsApikeys from "./Pages/Documentation/DocsApikeys.js";
import DocsCreators from "./Pages/Documentation/DocsCreator.js";
import DocsBasics from "./Pages/Documentation/DocsBasics.js";
import BlitzReleaseNotes from "./Pages/Misc/ReleaseNotes/BlitzReleaseNotes.js";
import BlitzAuth from "./CreatorUser/blitzauth.js";
import DocsOAuth from "./Pages/Documentation/DocsOAuth.js";
import Apps from "./CreatorUser/CreatorPages/Dashboard/apps.js";
import AppSpec from "./CreatorUser/CreatorPages/Dashboard/appSpec.js";
import CreatorOnboardApp from "./CreatorUser/creatorOnboardApp.js";
import { useQuery } from "react-query";
import ForgotPassword from "./Pages/PasswordForgot/forgotPassword.js";
import RestorePassword from "./Pages/PasswordForgot/restorePassword.js";
import CampaignOfferPage from "./Pages/Dashboard/Campaigns/individualcampaigns/offerpagecreator.js";
import UserProfile from "./Pages/Dashboard/Users/userprofile.js";
import ConversationsHub from "./Pages/Dashboard/Users/conversationhub.js";
import PublicUserProfile from "./Pages/Dashboard/Users/pubUser.js";
import CreatorInvoicingPage from "./CreatorUser/CreatorPages/Dashboard/creatorinvoicing.js";
import BlitzSplit from "./Pages/BlitzPay/BlitzSplit.js";
import InternalBookingPage from "./Pages/Dashboard/Search/steps/partnershipsbooking.js";
import RedirectWithQuery from "./Components/RedirectWithQuery.js";
import AgencyCreatorEdit from "./Pages/Agency/agencyCreatorTabs/agencyCreatorEdit.js";
import AgencyCreatorPayouts from "./Pages/Agency/agencyCreatorTabs/agencyPayoutCreator.js";
import AgencyCreatorCampaign from "./Pages/Agency/agencyCreatorTabs/agencyCreatorCampaign.js";
import AgencyPartnershipPage from "./Pages/Agency/agencyCreatorTabs/agencyPartnershipDialog.js";
import TermsOfServiceBusinessEntities from "./Pages/Home/forcreators/agencyterms.js";
import VendorSignupPage from "./Pages/Home/auxLinks/vendorSIgnup.js";
import AgencyCreatorCollaborations from "./Pages/Agency/agencyCreatorTabs/agencyCreatorSpecCollaborations.js";
import InternalLoginUser from "./CreatorUser/internalLoginUser.js";
import BlitzBoltAchievement from "./Pages/Dashboard/dashboard/blitzboltSpec.js";
import CreatorConversationsHub from "./CreatorUser/CreatorPages/Dashboard/creatorconvos.js";
import UnifiedLogin from "./Pages/Login/UnitedLogin.js";
import FileViewEdit from "./Pages/Files/fileviewedit.js";
import CampaignReport from "./Pages/Report/CampaignReport.js";
import BioPage from "./Bio/creatorbio.js";
import CreatorBio from "./CreatorUser/CreatorPages/Dashboard/Bio/creatorbio.js";
import JoinConversationPage from "./Pages/Conversations/joinconvo.js";
import CreatorPartnerPresentation from "./CreatorUser/CreatorPages/old_creatorpartner.js";
import PartnerSuite from "./CreatorUser/CreatorPages/Dashboard/partnersuite.js";
import IncomingPitches from "./CreatorUser/CreatorPages/Dashboard/Pitch/incoming.js";
import OutgoingPitches from "./CreatorUser/CreatorPages/Dashboard/Pitch/outgoing.js";
import InternalDocRoute from "./Pages/InternalDocs/InternalDocRoute.js";
import InternalHome from "./Pages/InternalDocs/InternalHome.js";
import InternalFrontend from "./Pages/InternalDocs/InternalFrontend.js";
import InternalBackend from "./Pages/InternalDocs/InternalBackend.js";
import AccountManagementDashboard from "./Pages/AccountManagement/amDash.js";
import ProductManagement from "./Pages/Commerce/ProductManagement.js";
import CommerceTransactions from "./Pages/Commerce/CommerceTransactions.js";
import ProductCheckoutPage from "./Pages/Commerce/ProductCheckoutPage.js";
import CommerceEmbeddedCheckoutPage from "./Pages/Commerce/CommerceEmbeddedCheckoutPage.js";
import CommerceEmbeddedCheckoutCompletePage from "./Pages/Commerce/CommerceEmbeddedCheckoutCompletePage.js";
import NewUserPresentation from "./Pages/Misc/newUserPresentation.js";
import ContactDashboard from "./GlobalPages/Contacts/contactdash.js";
import AgencyOutbound from "./Pages/Agency/agencyOutbound.js";
import CreatorBioTab from "./Pages/Agency/agencyCreatorTabs/agencyCreatorBio.js";
import CampaignCatalog from "./Pages/Dashboard/Campaigns/individualcampaigns/catalog.js";
import PayoutClaimPage from "./Pages/BlitzPay/claimpayout.js";
import ClaimFundsPage from "./Pages/ClaimFunds/ClaimFundsPage.js";
import InvestorRelations from "./Pages/Home/investors.js";
import Collaborate from "./Pages/Home/brandbenefits/collaborate.js";
import Create from "./Pages/Home/brandbenefits/create.js";
import { Capacitor } from "@capacitor/core";
import AppShell from "./AppShell.js";
import {
  PaymentSuccess,
  PaymentCancelled,
  PaymentError,
} from "./Components/payments/PaymentResult.js";

import PaymentSuccessRouter from "./Components/payments/PaymentRouter.js";

import MediaKit from "./Pages/MediaKit/mediaKit.js";
import CastingCalls from "./Pages/Dashboard/Campaigns/castingcalls/castingcalls.js";
import PushListener from "./Components/PushListener.js";
import { SplashScreen } from "@capacitor/splash-screen";
import { useCreatorAuth } from "./Hooks/creator-use-auth";
import TermsOfServiceCreators from "./Pages/Home/forcreators/creatorterms.js";
import DeleteAccount from "./Pages/PasswordForgot/deleteAccount.js";
import { Delete } from "@mui/icons-material";
import { PushNotifications } from "@capacitor/push-notifications";
import { GlobalStyles } from "@mui/material";
import AppStorePrompt from "./Pages/Misc/appstoreprompt.js";
import ApnsTokenDebug from "./Components/ApnsTokenDebug.js";
import DirectContractAccess from "./Pages/Contracts/accesscontract.js";
import EnhancedAgencyDealFlow from "./Pages/Agency/agencyPages/AgencyDealFlow.js";
import AgencyRosterSettings from "./Pages/Agency/agencyPages/AgencyRoster.js";
import AssetOrganizersPage from "./Pages/Files/AssetOrganizer.js";
import OrganizerDetailPage from "./Pages/Files/OrganizerDetail.js";
import PublicOrganizerPage from "./Pages/Files/PublicAccessOrganizer.js";
import PayPage from "./Pages/BlitzPay/paypage.js";
import PublicThreadPage from "./CreatorUser/CreatorPages/Dashboard/zapfeed/postpage.js";
import TrophyRoom from "./GlobalPages/TrophyRoom.js";
import GlobalLayout from "./Components/GlobalLayout.js";
import { IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GlobalUserInvite from "./GlobalPages/GlobalUserInvite.js";
import GlobalUserProfile from "./GlobalPages/globalprofile.js";
import AccountLinkingWizard from "./GlobalPages/MergeComponents/AccountLink.js";
import AgencyInvoiceDialog from "./Pages/Agency/agencyPages/invoicingWizard.js";
import ClientProfile from "./Pages/AccountManagement/pages/clientSpec.js";
import MyReferralsPage from "./GlobalPages/MyReferrals.js";
import ExternalContractAccess from "./Pages/Contracts/externalcontractaccess.js";
import Articles from "./Pages/Home/articles/article.js";
import ArticleDetail from "./Pages/Home/articles/articlespec.js";
import ButtPayLanding from "./Pages/Misc/buttpaylanding.js";
import AMTracking from "./Pages/AccountManagement/AMtracking.js";
import PublicNOAView from "./Pages/BlitzPay/extras/publicnoaview.js";
import BlitzT2Landing from "./Pages/Misc/t2landing.js";
import CastingCallPublicPage from "./Pages/Dashboard/Campaigns/castingcalls/castingcallpublic.js";
import BubblePublicPage from "./Bubbles/bubblepublicpage.js";
import PartnerOnboard from "./CreatorUser/newpartnerpres.js";
import ManageCastingCall from "./Pages/Dashboard/Campaigns/castingcalls/ManageCastingCall.js";
import PayPalCallback from "./Pages/PayPalCallback.js";
import StripeCallback from "./Pages/StripeCallback.js";
import BubbleChatPage from "./Bubbles/BubbleChatPage.js";
import NotificationsPage from "./GlobalPages/NotificationsPage.js";
import AgencyAllCollabs from "./Pages/Agency/agencyPages/AgencyAllCollabs.js";
import AgencyCreatorAllActivity from "./Pages/Agency/agencyCreatorTabs/AgencyCreatorAllActivity.js";
import AgencyAllMoney from "./Pages/Agency/agencyPages/AgencyAllMoney.js";
import { SocketProvider } from "./Context/SocketContext.js";
import { BannerProvider } from "./Context/BannerContext.js";
import TrackerPublicPage from "./Pages/Trackers/TrackerPublicView.js";
import BlitzSplitJungleEdition from "./Pages/Misc/blitzsplitjungleedition.js";
import PayRequestPage from "./Pages/BlitzPay/t2a/PayRequestPage.js";
import PayRequestSuccessPage from "./Pages/BlitzPay/t2a/PayRequestSuccessPage.js";

const Home = React.lazy(() => import("./Pages/Home/Home"));

// React Query client with optimized caching configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Frontend staleTime MUST be < Backend Redis TTL
      staleTime: 30000,              // 30 seconds (optimized for KPI staggering)
      cacheTime: 300000,             // 5 minutes (keep cached data)
      refetchOnWindowFocus: false,   // Prevent excessive refetches
      refetchOnReconnect: true,      // Refetch on network reconnect
      refetchInterval: false,        // Disable automatic polling
      networkMode: 'online',         // Standard online mode
      retry: 2,                      // Retry failed requests twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      // Request deduplication enabled by default (identical queries share same request)
    },
    mutations: {
      retry: 1,                      // Retry mutations once
    },
  },
});

const IOS_GOOGLE_CLIENT_ID =
  "489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com";
const WEB_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const googleClientId =
  Capacitor.getPlatform() === "ios"
    ? IOS_GOOGLE_CLIENT_ID
    : WEB_GOOGLE_CLIENT_ID;

const NAV_HEIGHT = 86; // ← must match BAR_HEIGHT in AppShell
const EXTRA_GUTTER = 8; // the "slight" extra spacing the team asked for
/**
 * Utility functions for domain checking
 */

export const isButtPayDomain = () => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  return hostname === 'www.buttpay.com' || 
         hostname === 'buttpay.com' ||
         (process.env.NODE_ENV === 'development' && 
          window.location.search.includes('buttpay=true')); // For local testing
};

export const isBlitzDomain = () => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  return !isButtPayDomain();
};

function RootRedirect() {
  const { isAuthorized: userOK } = useAuth();
  const { isAuthorized: creatorOK, isInitialized } = useCreatorAuth();

  // Wait for creator auth to initialize before making redirect decisions
  if (!isInitialized) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignments="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if we're on ButtPay domain - always redirect to ButtPay landing
  if (isButtPayDomain()) {
    return <Navigate to={routes.buttPay} replace />;
  }

  // For Blitz domain, use auth-based redirects
  if (userOK) return <Navigate to={routes.dashboard} replace />;
  if (creatorOK) return <Navigate to={routes.creatorConnectStart} replace />;
  const isBaseRoute = window.location.pathname === routes.base;

  if (isBaseRoute) {
    return <Navigate to={routes.home} replace />;
  }

  window.location.href = routes.base;
  return null;
}

function App() {
  useEffect(() => {
    if (Capacitor.getPlatform() !== "web") {
      SplashScreen.hide();
    }
  }, []);
  return (
    <HelmetProvider>
      {" "}
      {/* Wrap the app with HelmetProvider */}
      <QueryClientProvider client={queryClient}>
        <BannerProvider>
          <AppContent />
        </BannerProvider>
        <Analytics />
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const { isAuthorized, getCurrrentUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, creatorToken } = useCreatorAuth();
  
  const userInfo = getCurrrentUser();
  
  // Default to light mode instead of system preference
  const prefersDarkMode = false; // Changed: always default to light mode

  // Load user settings for both regular users and creators
  const { data: userSettings } = useQuery(
    ["userSettings"],
    client.users.fetchUserSettings,
    { 
      enabled: isAuthorized || isCreatorAuthorized, // Enable for both user and creator accounts
      staleTime: 1000 * 60 * 60 * 12,
      refetchOnWindowFocus: false,
    },
  );

  const isMobile = useMediaQuery("(max-width:960px)");
  
  // Theme configuration with proper defaults
  const theme = useMemo(() => {
    // Default to light mode (false) instead of system preference
    const effectiveDarkMode = userSettings?.dark_mode ?? false; // Changed: default to false
    const fontPreference = userSettings?.font_preference || "default";

    const fontFamily = {
      roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
      "open-sans": '"Open Sans", sans-serif',
      montserrat: '"Montserrat", sans-serif',
      default: "Inter, Arial, sans-serif",
    }[fontPreference];

    return getTheme({
      darkMode: effectiveDarkMode,
      customColors: userSettings?.theme_colors,
      density: userSettings?.ui_density || "comfortable", // Added default density
      fontFamily: fontFamily,
    });
  }, [userSettings, isAuthorized, isCreatorAuthorized]);

  // Apply font preference for both users and creators
  useEffect(() => {
    if (userSettings?.font_preference) {
      const fontFamily = {
        roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
        "open-sans": '"Open Sans", sans-serif',
        montserrat: '"Montserrat", sans-serif',
        default: "Inter, Arial, sans-serif", // Added default fallback
      }[userSettings.font_preference];
      if (fontFamily) {
        document.body.style.fontFamily = fontFamily;
      }
    } else if (isAuthorized || isCreatorAuthorized) {
      // Apply default font family when no preference is set
      document.body.style.fontFamily = "Inter, Arial, sans-serif";
    }
  }, [userSettings?.font_preference, isAuthorized, isCreatorAuthorized]);

  // ═══════════════════════════════════════════════════════════
  // TUTORIAL SYSTEM - User Identifier Logic
  // ═══════════════════════════════════════════════════════════
  const userIdentifier = useMemo(() => {
    if (isCreatorAuthorized && creatorToken?.creator_user?.username) {
      return `creator_${creatorToken.creator_user.username}`;
    } else if (isAuthorized && userInfo?.id) {
      return `user_${userInfo.id}`;
    }
    return null;
  }, [isAuthorized, isCreatorAuthorized, userInfo, creatorToken]);

  // SENTRY USER CONTEXT - Track authenticated users
  useEffect(() => {
    if (isCreatorAuthorized && creatorToken?.creator_user) {
      Sentry.setUser({
        id: creatorToken.creator_user.id?.toString(),
        email: creatorToken.creator_user.email,
        username: creatorToken.creator_user.username,
        role: 'creator',
      });
    } else if (isAuthorized && userInfo) {
      Sentry.setUser({
        id: userInfo.id?.toString(),
        email: userInfo.email,
        username: userInfo.name,
        role: 'user',
      });
    } else {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [isAuthorized, isCreatorAuthorized, userInfo, creatorToken]);

  const toggleHelpDialog = () => setHelpDialogOpen(!helpDialogOpen);

  return (
    <SentryErrorBoundary>
      <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <GlobalStyles
        styles={{
          ':root': { '--soc2-banner-height': '0px' },
          '.MuiAppBar-positionFixed': {
            top: 'var(--soc2-banner-height, 0px) !important',
          },
          '.MuiDrawer-docked > .MuiDrawer-paper': {
            top: 'var(--soc2-banner-height, 0px) !important',
            height: 'calc(100% - var(--soc2-banner-height, 0px)) !important',
          },
        }}
      />

      <AppStorePrompt />
      <PushListener />

      {false && (
        <Fab
          variant="contained"
          size="large"
          sx={(theme) => ({
            position: "fixed",
            bottom: theme.spacing(2),
            left: theme.spacing(2),
            backgroundColor: theme.palette.mode == "dark" ? "#222" : "#eee",
            color: theme.palette.mode == "dark" ? "#eee" : "#222",
            zIndex: theme.zIndex.drawer + 1,
          })}
          onClick={() => {
            setIsDark(!isDark);
          }}
        >
          {!isDark && <LightModeIcon></LightModeIcon>}
          {isDark && <DarkModeIcon></DarkModeIcon>}
        </Fab>
      )}
      {false && (
        <Fab
          variant="contained"
          size="large"
          sx={(theme) => ({
            position: "fixed",
            bottom: theme.spacing(2),
            left: theme.spacing(2), // Adjust to place it next to the light/dark mode button
            zIndex: theme.zIndex.drawer + 1,
          })}
          onClick={toggleHelpDialog}
        >
          <HelpIcon />
        </Fab>
      )}
      {/* Renders toast.success/error calls app-wide (react-toastify has no
          container mounted anywhere else — without this, toasts are dropped) */}
      <ToastContainer position="bottom-right" autoClose={4000} newestOnTop />
      <CreatorAuthProvider>
        {/* ═══════════════════════════════════════════════════════════
            TUTORIAL PROVIDER - Wraps entire routing system
            ═══════════════════════════════════════════════════════════ */}
        <BrowserRouter>
          <Soc2Banner />
          <StripeOnboardingReturnHandler />
          <SocketProvider>
  <TutorialProvider userIdentifier={userIdentifier}>
    <HelpDialog open={helpDialogOpen} onClose={toggleHelpDialog} />
    
    <TutorialOverlay />
    
    <Routes>
              <Route element={<AppShell />}>
                {/* Creator Connect Routes */}
                <Route
                  element={<CreatorConnectRoute onHelp={toggleHelpDialog} />}
                >
                  <Route
                    path={routes.creatorConnectOauth}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Authorize - Blitz" />
                        <BlitzAuth />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectStart}
                    element={
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <CreatorRoute>
                          <PageTitle title="Creator Start - Blitz" />
                          <CreatorStart />
                        </CreatorRoute>
                      </GoogleOAuthProvider>
                    }
                  />
                  <Route
                    path={routes.creatorConnectPayoutsInvoicing}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Payouts & Invoicing - Blitz" />
                        <PayoutsInvoicing />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectInternalTransfer}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Blitz T2" />
                        <InternalTransfer />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectPitch}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Outbound - Blitz" />
                        <Pitch />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectEdit}
                    element={
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <CreatorRoute>
                          <PageTitle title="Edit Creator Account - Blitz" />
                          <EditCreator />
                        </CreatorRoute>
                      </GoogleOAuthProvider>
                    }
                  />
                  <Route
                    path={routes.creatorConnectDealsEmails}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Inbound - Blitz" />
                        <BrandEmails />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectMore}
                    element={
                      <CreatorRoute>
                        <PageTitle title="More - Blitz" />
                        <CreatorMore />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorCampaigns}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Manage Campaign - Blitz" />
                        <CreatorSpecificCampaign />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creaotrParntershipsSpecific}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Manage Partnership - Blitz" />
                        <CreatorSpecificPartnership />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.pitchFromZap}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Pitch to a Project - Blitz" />
                        <PitchFromZap />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorCollabs}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Your Collaborations - Blitz" />
                        <CreatorCollaborations />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectApps}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Apps - Blitz" />
                        <Apps />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConnectAppsSpec}
                    element={
                      <CreatorRoute>
                        <PageTitle title="App - Blitz" />
                        <AppSpec />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorInvoicing}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Send a Blitz Invoice" />
                        <CreatorInvoicingPage />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.creatorConvos}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Creator Convos - Blitz" />
                        <CreatorConversationsHub />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.partnerSuite}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Partner VIP  - Blitz" />
                        <PartnerSuite />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.partnerSuiteIncoming}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Incoming Pitches  - Blitz" />
                        <IncomingPitches />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.partnerSuiteOutgoing}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Outgoing Pitches - Blitz" />
                        <OutgoingPitches />
                      </CreatorRoute>
                    }
                  />
                  <Route
                    path={routes.contactDash}
                    element={
                      <CreatorRoute>
                        <PageTitle title="Contacts  - Blitz" />
                        <ContactDashboard />
                      </CreatorRoute>
                    }
                  />
                </Route>

                {/* Private Routes with Header */}
                <Route element={<BlitzRoute onHelp={toggleHelpDialog} />}>
                  <Route
                    path={routes.dashboard}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Dashboard - Blitz" />
                        <Dashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.user}
                    element={
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <PrivateRoute>
                          <PageTitle title="User Management - Blitz" />
                          <User />
                        </PrivateRoute>
                      </GoogleOAuthProvider>
                    }
                  />
                  <Route
                    path={routes.invoicing}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Invoicing - Blitz" />
                        <Invoicing />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.search}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Search - Blitz" />
                        <Search />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.blitzpay}
                    element={
                      <PrivateRoute>
                        <PageTitle title="BlitzPay" />
                        <BlitzPay />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.blitzpayInternalTransfer}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Blitz T2" />
                        <InternalTransfer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.blitzpayPayoutRouting}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Payout Routing - BlitzPay" />
                        <PayoutRouting />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.paypage}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Send a BlitzPay" />
                        <PayPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={routes.addCreators}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Add Creators - Blitz" />
                        <AddCreators />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.company}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Company List - Blitz" />
                        <CompanyList />
                      </PrivateRoute>
                    }
                  />

                   <Route
                    path={routes.amTracking}
                    element={
                      <PrivateRoute>
                        <PageTitle title="AM Tracking - Blitz" />
                        <AMTracking />
                      </PrivateRoute>
                    }
                  />
                  
                  <Route
                    path={routes.editCampaign}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Edit Campaign - Blitz" />
                        <EditCampaignPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.partnershipsPage}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Partnerships - Blitz" />
                        <PartnershipsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.trackers}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Trackers - Blitz" />
                        <Trackers />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.campaignconvospec}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Chat with Creator - Blitz" />
                        <CampaignCreatorSpecific />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.partnerships}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Partnerships - Blitz" />
                        <Navigate to={routes.collaborations} replace />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.castingCalls}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Casting Calls - Blitz" />
                        <CastingCalls />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.collaborations}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Collaborations - Blitz" />
                        <Collaborations />
                      </PrivateRoute>
                    }
                  />
        
                  <Route
                    path={routes.clientInvoicing}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Blitz Split" />
                        <BlitzSplit />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.creatorBooking}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Creator Booking" />
                        <InternalBookingPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.amDash}
                    element={
                      <PrivateRoute>
                        <PageTitle title="AM Dashboard - Blitz" />
                        <AccountManagementDashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.commerce}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Blitz Commerce - Blitz" />
                        <ProductManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.commerceTransactions}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Transactions - Blitz Commerce" />
                        <CommerceTransactions />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={routes.organizers}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Asset Organizers - Blitz" />
                        <AssetOrganizersPage />
                      </PrivateRoute>
                    }
                  />
              <Route element={<AgencyRoot />}>
                <Route
                  path={routes.agency}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Agency - Blitz" />
                      <AgencyStart />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={routes.agencySales}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Agency Sales - Blitz" />
                      <AgencyOutbound />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.agencyInvoice}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Agency Sales - Blitz" />
                      <AgencyInvoiceDialog />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.agencyAllCollabs}
                  element={
                    <PrivateRoute>
                      <PageTitle title="All Your Collabs - Blitz" />
                      <AgencyAllCollabs />
                    </PrivateRoute>
                  }
                />

              <Route
                path={routes.agencyAllMoney}
                element={
                  <PrivateRoute>
                    <PageTitle title="Agency Money - Blitz" />
                    <AgencyAllMoney />
                  </PrivateRoute>
                }
              />

                <Route
                  path={routes.agencyDealFlow}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Deal Flow - Blitz" />
                      <EnhancedAgencyDealFlow />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.agencyRosterSettings}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Roster Settings - Blitz" />
                      <AgencyRosterSettings />
                    </PrivateRoute>
                  }
                />

                {/* Standalone campaign route */}
                <Route
                  path={routes.agencyCreatorCampaign}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Campaign - Agency - Blitz" />
                      <AgencyCreatorCampaign />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.agencyCreatorPartnership}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Partnership - Agency - Blitz" />
                      <AgencyPartnershipPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.agencyCreators}
                  element={
                    <PrivateRoute>
                      <PageTitle title="My Agency Creators - Blitz" />
                      <AgencyCreators />
                    </PrivateRoute>
                  }
                >
                  <Route
                    path={routes.agencyCreator}
                    element={<AgencyCreator />}
                  >
                    {/* Default route - Activity tab */}
                    <Route index element={<AgencyCreatorAllActivity />} />
                    
                    {/* Bio tab */}
                    <Route
                      path={routes.agencyCreatorBio}
                      element={<CreatorBioTab />}
                    />
                    
                    {/* Collaborations sub-routes (for viewing specific items) */}
                    <Route
                      path="collaborations"
                    >
                      <Route
                        path="campaigns/:campaignId"
                        element={<AgencyCreatorCampaign />}
                      />
                      <Route
                        path="partnerships/:partnershipId"
                        element={<AgencyPartnershipPage />}
                      />
                    </Route>
                    
                    {/* Payouts route (if accessed from activity card) */}
                    <Route
                      path="payouts"
                      element={<AgencyCreatorPayouts />}
                    />
                  </Route>
                </Route>
              </Route>
                </Route>


                {/* Docs Pages */}
                <Route element={<DocsRoute />}>
                  <Route
                    path={routes.docsHome}
                    element={
                      <>
                        <PageTitle title="Blitz API Documentation" />
                        <DocsHomepage />
                      </>
                    }
                  />
                  <Route
                    path={routes.docsService}
                    element={
                      <>
                        <PageTitle title="1) Services - Blitz API Documentation" />
                        <DocsServices />
                      </>
                    }
                  />
                  <Route
                    path={routes.docsKeys}
                    element={
                      <>
                        <PageTitle title="2) API keys - Blitz API Documentation" />
                        <DocsApikeys />
                      </>
                    }
                  />
                  <Route
                    path={routes.docsOauth}
                    element={
                      <>
                        <PageTitle title="3) Login with Blitz - Blitz API Documentation" />
                        <DocsOAuth />
                      </>
                    }
                  />
                  <Route
                    path={routes.docsEndpoints}
                    element={
                      <>
                        <PageTitle title="Basic API Usage - Blitz API Documentation" />
                        <DocsBasics />
                      </>
                    }
                  />
                  <Route
                    path={routes.docsEndCreators}
                    element={
                      <>
                        <PageTitle title="Creator - Blitz API Documentation" />
                        <DocsCreators />
                      </>
                    }
                  />
                </Route>
                <Route
                  path={routes.payRequest}
                  element={
                    <>
                      <PageTitle title="Pay Request - Blitz" />
                      <PayRequestPage />
                    </>
                  }
                />
                <Route
                  path={routes.payRequestSuccess}
                  element={
                    <>
                      <PageTitle title="Payment Complete - Blitz" />
                      <PayRequestSuccessPage />
                    </>
                  }
                />
                <Route
                  path={routes.commerceCheckout}
                  element={
                    <>
                      <PageTitle title="Checkout - Blitz Commerce" />
                      <ProductCheckoutPage />
                    </>
                  }
                />
                <Route
                  path={routes.commerceEmbeddedCheckout}
                  element={
                    <>
                      <PageTitle title="Checkout - Blitz Commerce" />
                      <CommerceEmbeddedCheckoutPage />
                    </>
                  }
                />
                <Route
                  path={routes.commerceEmbeddedCheckoutComplete}
                  element={
                    <>
                      <PageTitle title="Payment Complete - Blitz Commerce" />
                      <CommerceEmbeddedCheckoutCompletePage />
                    </>
                  }
                />

                {/* Internal Docs Pages */}
                <Route element={<InternalDocRoute />}>
                  <Route
                    path={routes.intDocsHome}
                    element={
                      <>
                        <PageTitle title="Blitz Documentation" />
                        <InternalHome />
                      </>
                    }
                  />
                  <Route
                    path={routes.intDocsFrontend}
                    element={
                      <>
                        <PageTitle title="Frontend - Blitz Documentation" />
                        <InternalFrontend />
                      </>
                    }
                  />
                  <Route
                    path={routes.intDocsBackend}
                    element={
                      <>
                        <PageTitle title="Backend - Blitz Documentation" />
                        <InternalBackend />
                      </>
                    }
                  />
                </Route>

                {/* Public Pages */}
                {/* 1 — HOME  */}
                {/* <Route
                  path={routes.home}
                  element={
                    <>
                      <PageTitle title="Blitz - Pay, Create, Collaborate" />
                      <Main>
                        <Home />
                      </Main>
                    </>
                  }
                /> */}

                {/* 2 — DASHBOARD (keeps BlitzRoute + PrivateRoute) */}
                <Route element={<BlitzRoute onHelp={toggleHelpDialog} />}>
                  <Route
                    path={routes.dashboard}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Dashboard – Blitz" />
                        <Dashboard />
                      </PrivateRoute>
                    }
                  />
                </Route>

                {/* 3 — LOGIN (same web/native logic) */}
                <Route
                  path={routes.login}
                  element={
                    Capacitor.getPlatform() === "web" ? (
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <PageTitle title="Login – Blitz" />
                        <UnifiedLogin />
                      </GoogleOAuthProvider>
                    ) : (
                      <>
                        <PageTitle title="Login – Blitz" />
                        <UnifiedLogin />
                      </>
                    )
                  }
                />

               <Route path={routes.base} element={<RootRedirect />} />
               {/* PROXY failed or NOT CONNECTED */}
                <Route
                  path={routes.home}
                  element={
                    <>
                      <PageTitle title="Blitz - The Workforce For The New Economy" />
                      <Main>
                        <Home />
                      </Main>
                    </>
                  }
                />
                <Route
                  path={routes.tempCreator}
                  element={
                    <>
                      <PageTitle title="Your Temp page - Blitz" />
                      <TempCreatorPage />
                    </>
                  }
                />
                <Route
                  path={routes.campaignReport}
                  element={
                    <>
                      <PageTitle title="Campaign Details - Blitz" />
                      <CampaignReport></CampaignReport>
                    </>
                  }
                />
                <Route
                  path={routes.creatorMediaKit}
                  element={
                    <>
                      <PageTitle title="Creator Media Kit - Blitz" />
                      <MediaKit />
                    </>
                  }
                />

                <Route
                  path={routes.trackerPublic}          // "/tracker/:trackerId"
                  element={
                    <>
                      <PageTitle title="Content Tracker - Blitz" />
                      <TrackerPublicPage />
                    </>
                  }
                />
                
                <Route
                  path={`${routes.creatorMediaKit}/promotional/:discount`}
                  element={<MediaKit />}
                />
                <Route path={routes.roster} element={<AgencyDetailsPage />} />
                <Route
                  path={`${routes.creatorSignup}/:manager`}
                  element={
                    <>
                      <PageTitle title="Creator Signup - Blitz" />
                      <CreatorSignup />
                    </>
                  }
                />
                <Route
                  path={`${routes.creatorMediaKit}/similar`}
                  element={
                    <>
                      <PageTitle title="Similar Creators - Blitz" />
                      <SimilarCreators />
                    </>
                  }
                />
                <Route
                  path={`${routes.demoRegister}/:ref`}
                  element={
                    <>
                      <PageTitle title="Register Your Free Account - Blitz" />
                      <DemoRegister />
                    </>
                  }
                />
                <Route
                  path={routes.about}
                  element={
                    <>
                      <PageTitle title="About - Blitz" />
                      <BlitzSummary />
                    </>
                  }
                />
                <Route
                  path={routes.creatorCampaignTimeline}
                  element={
                    <>
                      <PageTitle title="Creator Campaign Timelines - Blitz" />
                      <CreatorCampaignTimelines />
                    </>
                  }
                />
                <Route
                  path={routes.deliverablePage}
                  element={
                    <>
                      <PageTitle title="Deliverables - Blitz" />
                      <CreatorDeliverables />
                    </>
                  }
                />
                <Route
                  path={`${routes.invoicing}/:invoiceId`}
                  element={
                    <>
                      <PageTitle title="Invoice - Blitz" />
                      <InvoicePage />
                    </>
                  }
                />
                <Route
                  path={`${routes.creatorPayoutInvoices}/:id`}
                  element={
                    <>
                      <PageTitle title="Creator Invoice - Blitz" />
                      <CreatorInvoicePage />
                    </>
                  }
                />
                <Route
                  path={routes.help}
                  element={
                    <>
                      <PageTitle title="Help - Blitz" />
                      <HelpCenter />
                    </>
                  }
                />
                {/* <Route
                  path={routes.solutions}
                  element={
                    <>
                      <PageTitle title="Blitz - Pay" />
                      <Solutions />
                    </>
                  }
                /> */}
                {/* <Route
                  path={routes.create}
                  element={
                    <>
                      <PageTitle title="Blitz - Create" />
                      <Create />
                    </>
                  }
                /> */}
                {/* <Route
                  path={routes.collaborate}
                  element={
                    <>
                      <PageTitle title="Blitz - Collaborate" />
                      <Collaborate />
                    </>
                  }
                /> */}
                <Route
                  path={routes.solutionsAgencies}
                  element={
                    <>
                      <PageTitle title="For Agencies - Blitz" />
                      <Agencies />
                    </>
                  }
                />
                <Route
                  path={routes.forcreators}
                  element={
                    <>
                      <PageTitle title="For Creators - Blitz" />
                      <ForCreators />
                    </>
                  }
                />
                <Route
                  path={routes.whypartner}
                  element={
                    <>
                      <PageTitle title="Why Partner? - Blitz" />
                      <WhyPartner />
                    </>
                  }
                />
                <Route
                  path={routes.parntersListL}
                  element={
                    <>
                      <PageTitle title="Our Creator Partners - Blitz" />
                      <PartnerCreatorRoster />
                    </>
                  }
                />
                <Route
                  path={routes.privacyPolicy}
                  element={
                    <>
                      <PageTitle title="Privacy Policy - Blitz" />
                      <PrivacyPolicy />
                    </>
                  }
                />
                <Route
                  path={routes.investors}
                  element={
                    <>
                      <PageTitle title="For Investors - Blitz" />
                      <InvestorRelations />
                    </>
                  }
                />

                <Route
                  path={routes.campaignOptOut}
                  element={
                    <>
                      <PageTitle title="Archive Campaign Proposal - Blitz" />
                      <OptOutCampaign />
                    </>
                  }
                />
                <Route
                  path={`${routes.payoutSpec}/:payoutId`}
                  element={
                    <>
                      <PageTitle title="Payout - Blitz" />
                      <PayoutPage />
                    </>
                  }
                />
                <Route
                  path={routes.manageDealsIO}
                  element={
                    <>
                      <PageTitle title="View Deal - Blitz" />
                      <DealIOPublic />
                    </>
                  }
                />
                <Route
                  path={routes.releasteNotes}
                  element={
                    <>
                      <PageTitle title="Blitz Release Notes" />
                      <BlitzReleaseNotes />
                    </>
                  }
                />
                <Route
                  path={routes.campaignOffer}
                  element={
                    <>
                      <PageTitle title="Campaign Offer - Blitz" />
                      <CampaignOfferPage />
                    </>
                  }
                />
                <Route
                  path={routes.PublicUserProfile}
                  element={
                    <>
                      <PageTitle title="View Public Profile - Blitz" />
                      <PublicUserProfile />
                    </>
                  }
                />
                <Route
                  path={routes.agencyTerms}
                  element={
                    <>
                      <PageTitle title="Blitz Terms of Service" />
                      <TermsOfServiceBusinessEntities />
                    </>
                  }
                />
                <Route
                  path={routes.creatorTerms}
                  element={
                    <>
                      <PageTitle title="Blitz Creator Terms of Service" />
                      <TermsOfServiceCreators />
                    </>
                  }
                />

                <Route
                  path="/blitzbolts/:userId/:attainmentId"
                  element={<BlitzBoltAchievement />}
                />
                <Route
                  path={routes.campaignPublic}
                  element={
                    <>
                      <PageTitle title="Blitz Campaign" />
                      <CampaignDetailsPage />
                    </>
                  }
                />
                <Route
                  path={routes.castingCallPublic}
                  element={
                    <>
                      <PageTitle title="Casting Call - Blitz" />
                      <CastingCallPublicPage />
                    </>
                  }
                />
                  <Route
                  path={routes.bubblePublic}
                  element={
                    <>
                      <PageTitle title="Bubble Page - Blitz" />
                      <BubblePublicPage />
                    </>
                  }
                />
                <Route
                  path={routes.deleteAccount}
                  element={
                    <>
                      <PageTitle title="Delete your account" />
                      <DeleteAccount />
                    </>
                  }
                />
                <Route
                  path={routes.userProfile}
                  element={
                    <>
                      <PageTitle title="View User Profile - Blitz" />
                      <UserProfile />
                    </>
                  }
                />

                <Route
                  path={routes.clientProfile}
                  element={
                    <>
                      <PageTitle title="View Client Profile - Blitz" />
                      <ClientProfile />
                    </>
                  }
                />

                <Route
                  path={routes.contractSpec}
                  element={
                    <>
                      <PageTitle title="Contract - Blitz" />
                      <DirectContractAccess />
                    </>
                  }
                />

                <Route
                  path={routes.bubbleChat}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Bubble Chat - Blitz" />
                      <BubbleChatPage />
                    </PrivateRoute>
                  }
                />

                {/* Alternative: Direct bubble access route */}
                <Route
                  path={routes.bubbleChatDirect}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Bubble Chat - Blitz" />
                      <BubbleChatPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path={routes.externalContractSpec}
                  element={
                    <>
                      <PageTitle title="Contract - Blitz" />
                      <ExternalContractAccess />
                    </>
                  }
                />
                <Route
                  path={routes.threadPage}
                  element={
                    <>
                      <PageTitle title="Thread - Blitz" />
                      <PublicThreadPage />
                    </>
                  }
                />

                {/* Sign Ups and Registers */}
                <Route
                  path={routes.login}
                  element={
                    Capacitor.getPlatform() === "web" ? (
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <PageTitle title="Login – Blitz" />
                        <UnifiedLogin />
                      </GoogleOAuthProvider>
                    ) : (
                      // on iOS/Android we just render our native‐ready UnifiedLogin
                      <>
                        <PageTitle title="Login – Blitz" />
                        <UnifiedLogin />
                      </>
                    )
                  }
                />
                <Route element={<BlitzRoute onHelp={toggleHelpDialog} />}>
                  <Route
                    path={routes.organizerDetail}
                    element={
                      <PrivateRoute>
                        <PageTitle title="Organizer Details - Blitz" />
                        <OrganizerDetailPage />
                      </PrivateRoute>
                    }
                  />
                   {/* NEW: Checklist-focused view */}
                <Route
                  path={routes.organizerChecklist}
                  element={
                    <PrivateRoute>
                      <PageTitle title="Organizer Checklist - Blitz" />
                      <OrganizerDetailPage initialTab={2} />
                    </PrivateRoute>
                  }
                />
                </Route>

                
                <Route
                  path={routes.accessfile}
                  element={
                    <>
                      <PageTitle title="Your File" />
                      <FileViewEdit />
                    </>
                  }
                />

                <Route
                  path={routes.register}
                  element={
                    <>
                      <PageTitle title="Register - Blitz" />
                      <Register />
                    </>
                  }
                />
                <Route
                  path={routes.passwordForgot}
                  element={
                    <>
                      <PageTitle title="Forgot my Password - Blitz" />
                      <ForgotPassword />
                    </>
                  }
                />
                <Route
                  path={routes.passwordRenew}
                  element={
                    <>
                      <PageTitle title="Forgot my Password - Blitz" />
                      <RestorePassword />
                    </>
                  }
                />
                <Route
                  path={`${routes.register}/:company`}
                  element={
                    <>
                      <PageTitle title="Register - Blitz" />
                      <Register />
                    </>
                  }
                />
                <Route
                  path={routes.requestAccess}
                  element={
                    <>
                      <PageTitle title="Request Access - Blitz" />
                      <RequestAccess />
                    </>
                  }
                />
                <Route
                  path={`${routes.requestAccess}/:ref`}
                  element={
                    <>
                      <PageTitle title="Request Access - Blitz" />
                      <RequestAccess />
                    </>
                  }
                />
                <Route
                  path={routes.creatorSignup}
                  element={
                    <>
                      <PageTitle title="Creator Signup - Blitz" />
                      <CreatorSignup />
                    </>
                  }
                />
                <Route
                  path={`${routes.vendorSignup}/:company`}
                  element={
                    <>
                      <PageTitle title="Vendor Signup - Blitz" />
                      <VendorSignupPage />
                    </>
                  }
                />

                <Route
                  path={routes.noaView}
                  element={
                    <>
                      <PageTitle title="View Notice of Assignment - Blitz" />
                      <PublicNOAView />
                    </>
                  }
                />

                <Route
                  path={routes.paypalCallback}
                  element={
                    <>
                      <PageTitle title="PayPal Connection - Blitz" />
                      <PayPalCallback />
                    </>
                  }
                />

                <Route
                  path={routes.stripeCallback}
                  element={
                    <>
                      <PageTitle title="Stripe Connection - Blitz" />
                      <StripeCallback />
                    </>
                  }
                />

                <Route
                  path={routes.articles}
                  element={
                    <>
                      <PageTitle title="Articles - Blitz" />
                      <Articles />
                    </>
                  }
                />
                <Route
                  path={routes.buttPay}
                  element={
                    <>
                      <PageTitle title="ButtPay" />
                      <ButtPayLanding />
                    </>
                  }
                />

                 <Route
                  path={routes.t2}
                  element={
                    <>
                      <PageTitle title="Blitz T2 - Treasury and Transmitter Layer" />
                      <BlitzT2Landing />
                    </>
                  }
                />
                <Route
                    path={routes.split}
                    element={
                      <>
                        <PageTitle title="Blitz Split - Enterprise to Business to Vendor" />
                        <BlitzSplitJungleEdition />
                      </>
                    }
                  />
                 <Route
                  path={routes.articlesSpec}
                  element={
                    <>
                      <PageTitle title="Read this Article - Blitz" />
                      <ArticleDetail />
                    </>
                  }
                />
                <Route
                  path="/articles/streamline-campaigns"
                  element={
                    <>
                      <PageTitle title="Streamline Your Influencer Campaigns - Blitz" />
                      <StreamlineCampaigns />
                    </>
                  }
                />
                <Route
                  path="/articles/future-payments"
                  element={
                    <>
                      <PageTitle title="Future of Creator and Vendor Payments - Blitz" />
                      <FutureOfPayments />
                    </>
                  }
                />
                <Route
                  path="/articles/hottest-software"
                  element={
                    <>
                      <PageTitle title="The Hottest New Free Creator Software - Blitz" />
                      <HottestNewCreatorSoftware />
                    </>
                  }
                />
                <Route
                  path={routes.onboard}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Onboard - Blitz" />
                      <AllOnboard />
                    </GoogleOAuthProvider>
                  }
                />
                {/* Bio Pages - both formats supported */}
                <Route
                  path={routes.bioAtSlug}  // NEW: /username format
                  element={
                    <>
                      <PageTitle title="Bio - Blitz" />
                      <BioPage />
                    </>
                  }
                />
                <Route
                  path={routes.bioPublic}  // EXISTING: /b/username format
                  element={
                    <>
                      <PageTitle title="Bio - Blitz" />
                      <BioPage />
                    </>
                  }
                />

                <Route
                  path={routes.joinConvo}
                  element={
                    <>
                      <PageTitle title="Join Conversation Blitz" />
                      <JoinConversationPage />
                    </>
                  }
                />
                <Route
                  path={routes.campaignCatalog}
                  element={
                    <>
                      <PageTitle title="Creator Catalog" />
                      <CampaignCatalog />
                    </>
                  }
                />
                <Route
                  path={routes.claimPayout}
                  element={
                    <>
                      <PageTitle title="Claim Your BlitzPay" />
                      <PayoutClaimPage />
                    </>
                  }
                />
                <Route
                  path={routes.claimFunds}
                  element={
                    <>
                      <PageTitle title="Claim Funds - Blitz" />
                      <ClaimFundsPage />
                    </>
                  }
                />
                <Route
                  path={routes.publicOrganizer}
                  element={
                    <>
                      <PageTitle title="Document Organizer - Blitz" />
                      <PublicOrganizerPage />
                    </>
                  }
                />

                <Route
                  path={routes.creatorConnectGoogle}
                  element={
                    <>
                      <PageTitle title="Redirect - Blitz" />
                      <CreatorRedirect />
                    </>
                  }
                />
                <Route
                  path={routes.creatorConnectOnboard}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Onboard - Blitz" />
                      <AllOnboard />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.creatorConnectOnboardApp}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Onboard with App - Blitz" />
                      <CreatorOnboardApp />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.creatorConnectReauth}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Reauthenticate your Creator Connect Account - Blitz" />
                      <CreatorConnectReatuh />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.creatorConnect}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Creator Connect - Blitz" />
                      <CreatorConnect />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.creatorLogin}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="Creator Login - Blitz" />
                      <RedirectWithQuery
                        to={routes.login}
                        newQuery={{ tab: "creator" }}
                      />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.newCreatorPartnerPresentation}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="VIP+ Offer" />
                      <PartnerOnboard />
                    </GoogleOAuthProvider>
                  }
                />
                <Route
                  path={routes.globalUserInvite}
                  element={
                    <>
                      <PageTitle title="Join Blitz - Invite" />
                      <GlobalUserInvite />
                    </>
                  }
                />
                <Route
                  path={routes.claimSignup}
                  element={
                    <>
                      <PageTitle title="Join Blitz" />
                      <GlobalUserInvite />
                    </>
                  }
                />
                <Route
                  path={routes.newUserPresentation}
                  element={
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <PageTitle title="VIP+ User Offer" />
                      <NewUserPresentation />
                    </GoogleOAuthProvider>
                  }
                />

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.trophyRoom} 
                    element={
                      <>
                        <PageTitle title="Trophy Room - Blitz" />
                        <TrophyRoom />
                      </>
                    } 
                  />
                  </Route>

                  <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.notifications} 
                    element={
                      <>
                        <PageTitle title="Notifications - Blitz" />
                        <NotificationsPage />
                      </>
                    } 
                  />
                </Route>

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>

                  <Route
                    path={routes.conversationshub}
                    element={
                                            <>

                        <PageTitle title="Conversations - Blitz" />
                        <ConversationsHub />
                                            </>

                   }
                  />
                </Route>


                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.myReferrals} 
                    element={
                      <>
                        <PageTitle title="My Referrals - Blitz" />
                        <MyReferralsPage />
                      </>
                    } 
                  />
                </Route>

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.globalProfile} 
                    element={
                      <>
                        <PageTitle title="Global Profile - Blitz" />
                        <GlobalUserProfile />
                      </>
                    } 
                  />
                  {/* Add any other global routes here following the same pattern */}
                </Route>

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.files}
                    element={
                      <>
                        <PageTitle title="Your Files - Blitz" />
                        <AssetsManager />
                      </>
                    } 
                  />
                  {/* Add any other global routes here following the same pattern */}
                </Route>

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.creatorbio}
                    element={
                      <>
                        <PageTitle title="Your Blitz Bio" />
                        <CreatorBio />
                      </>
                    } 
                  />
                  {/* Add any other global routes here following the same pattern */}
                </Route>

                <Route element={<GlobalRoute onHelp={toggleHelpDialog} />}>
                  <Route 
                    path={routes.manageCastingCall} 
                    element={
                      <>
                        <PageTitle title="Manage Casting Call - Blitz" />
                        <ManageCastingCall />
                      </>
                    } 
                  />
                  </Route>
  

                <Route 
                    path={routes.accountLinker} 
                    element={
                      <>
                        <PageTitle title="Account Linking - Blitz" />
                        <AccountLinkingWizard />
                      </>
                    } 
                  />
                
                {/* Payment Result Routes */}
                <Route
                  path="/payment/success"
                  element={
                    <>
                      <PageTitle title="Payment - Blitz" />
                      <PaymentSuccessRouter />
                    </>
                  }
                />

                
                <Route
                  path="/payment/cancelled"
                  element={
                    <>
                      <PageTitle title="Payment Cancelled - Blitz" />
                      <PaymentCancelled />
                    </>
                  }
                />
                <Route
                  path="/payment/error"
                  element={
                    <>
                      <PageTitle title="Payment Error - Blitz" />
                      <PaymentError />
                    </>
                  }
                />

                {/* Internal Login for Testing */}
                {process.env.NODE_ENV === "development" && (
                  <Route
                    path={routes.internalLogin}
                    element={
                      <>
                        <PageTitle title="Creator Login - Blitz" />
                        <InternalLogin />
                      </>
                    }
                  />
                )}
                {process.env.NODE_ENV === "development" && (
                  <Route
                    path={routes.internalUserLogin}
                    element={
                      <>
                        <PageTitle title="Login - Blitz" />
                        <InternalLoginUser />
                      </>
                    }
                  />
                )}
                <Route
                  path="*"
                  element={
                    <>
                      <PageTitle title="404 Not Found - Blitz" />
                      <Navigate to={routes.base} replace />
                    </>
                  }
                />
              </Route>
              
            </Routes>
          </TutorialProvider>  {/* ← Move this inside */}
  </SocketProvider>
        </BrowserRouter>
      </CreatorAuthProvider>
    </ThemeProvider>
    </SentryErrorBoundary>
  );
}

export default App;